import type { Flight, LogbookEntry } from '@/lib/db';

import { timeConsistencyIssues } from './time-checks';

/**
 * Whether this entry's takeoff/landing counts need a pilot's eyes — flagged
 * red in the grid as a warning, not a hard block on approving — per
 * `NZ-LOGBOOK.md` "Nothing is auto-committed" applied to the two numbers
 * that feed the passenger-carrying recency check in §5, where a wrong count
 * is a compliance risk, not just a stray digit.
 *
 * Two independent triggers, either one enough on its own:
 *  - **the totals disagree.** Every takeoff should be matched by a landing —
 *    a touch-and-go is a landing *and* a takeoff, never just one — so any
 *    other outcome is worth a second look, whether the numbers were typed or
 *    drafted from a track.
 *  - **no usable source data, still unreviewed.** The count is still the
 *    track's own guess (`derivedFields` — `draft.ts` only marks a field this
 *    way when it actually wrote a non-zero guess into it) and the track it
 *    came from never had enough to go on (`DerivedStats.confidence ===
 *    'low'`): no timestamps at all, a recording that broke where something
 *    might have happened, or a count `ground-events.ts` had to infer rather
 *    than read off an actual ground contact — from a nearby aerodrome at
 *    either end of the recording (`edgeEventAerodrome`) or from a gap in
 *    coverage over a runway (`runwayGapAt`) — the ways of the detector not
 *    quite having seen what it is reporting, so the pilot is the one who
 *    knows what actually happened. A `'partial'` track was read end to end and every
 *    count on it came from a ground contact the fixes show — that's the
 *    ordinary case for every import, not a reason to flag every drafted row
 *    regardless of what it actually found. This clears the moment the pilot
 *    types over one of the four cells, the same "edit confirms it" rule the
 *    rest of the grid already uses for derived fields.
 */
const TAKEOFF_LANDING_FIELDS = [
  'takeoffs.day',
  'takeoffs.night',
  'landings.day',
  'landings.night',
] as const;

export function takeoffLandingTotalsMismatch(entry: LogbookEntry): boolean {
  return entry.takeoffs.day + entry.takeoffs.night !== entry.landings.day + entry.landings.night;
}

export function hasUnreviewedLowQualityCounts(
  entry: LogbookEntry,
  flight: Flight | undefined,
): boolean {
  const stillDerived = TAKEOFF_LANDING_FIELDS.some((field) => entry.derivedFields.includes(field));
  if (!stillDerived) {
    return false;
  }
  return flight === undefined || flight.derived.confidence === 'low';
}

export function needsTakeoffLandingReview(
  entry: LogbookEntry,
  flight: Flight | undefined,
): boolean {
  return takeoffLandingTotalsMismatch(entry) || hasUnreviewedLowQualityCounts(entry, flight);
}

/** Plain-language reason shown in a flagged cell's tooltip — mismatch takes priority since it's the more actionable of the two. */
export function takeoffLandingReviewReason(
  entry: LogbookEntry,
  flight: Flight | undefined,
): string | undefined {
  if (takeoffLandingTotalsMismatch(entry)) {
    return 'Takeoffs and landings do not add up to the same total.';
  }
  if (hasUnreviewedLowQualityCounts(entry, flight)) {
    return 'Count estimated from the track.';
  }
  return undefined;
}

/**
 * Every cell on this row that the grid should paint red, mapped to the reason
 * its tooltip shows.
 *
 * Two independent checks feed it — the takeoff/landing one above, and the
 * time arithmetic in `time-checks.ts` — and they overlap on no cell today,
 * but a cell reached by both keeps the first reason: the list is ordered by
 * what a pilot would fix first, and a tooltip that changes depending on which
 * check ran last would be worse than either message alone.
 *
 * An empty map is a clean row. That is also the grid's "does this entry need
 * review" test, so a new check added here starts flagging the approve button
 * without anything else having to be told about it.
 */
export function reviewFlags(
  entry: LogbookEntry,
  flight: Flight | undefined,
): ReadonlyMap<string, string> {
  const flags = new Map<string, string>();

  const countReason = takeoffLandingReviewReason(entry, flight);
  if (countReason !== undefined) {
    for (const field of TAKEOFF_LANDING_FIELDS) {
      flags.set(field, countReason);
    }
  }

  for (const issue of timeConsistencyIssues(entry)) {
    for (const field of issue.fields) {
      if (!flags.has(field)) {
        flags.set(field, issue.message);
      }
    }
  }

  return flags;
}

export { TAKEOFF_LANDING_FIELDS };
